// Hücre notu iş parçacığı (portal yetki v2 Dalga 3, spec §6/§8.3/§8.4): kronolojik mesaj listesi +
// (isteğe bağlı) yazma alanı. İki yüz aynı bileşeni kullanır — `audience` yalnız uç yolunu ve kopyayı
// seçer; görünürlük/yetki SUNUCUDA her istekte yeniden doğrulanır (403 → sakin erişim metni).
// Portalda danışman mesajı "Danışman yanıtı:" önekiyle ve ayrı çerçeveyle (`kpi-note--consultant`).
// Gönderim: `clientRequestId` yeniden denemede AYNI kalır (sunucu tekilleştirir); metin değişince yeni
// kimlik alınır (aksi halde sunucu eski satırı döndürüp yeni metni yutardı). 429 → sunucunun Türkçe
// `message`'ı. Açılışta karşı tarafın mesajları "okundu" işaretlenir (özet noktası söner).

import { useEffect, useRef, useState } from "react";
import type { KpiValueNoteDto } from "@mahir/shared";
import {
  createCellNote,
  listCellNotes,
  markCellNotesSeen,
  type NoteCellRef,
  type NotesAudience,
} from "../../api/kpi-notes";
import { KPI_NOTES_COPY } from "../../lib/kpi-notes-copy";
import { NoteRequestId, noteAuthorLabel, noteErrorMessage, noteTone } from "../../lib/kpi-notes-ui";
import { cellInputStyle as inp } from "./kpi-form-styles";

const TONE_STYLE: Record<ReturnType<typeof noteTone>, React.CSSProperties> = {
  consultant: {
    background: "color-mix(in srgb, var(--color-primary) 8%, white)",
    border: "1px solid color-mix(in srgb, var(--color-primary) 30%, white)",
    borderLeft: "3px solid var(--color-primary)",
  },
  mine: { background: "var(--color-bg)", border: "1px solid var(--color-border)" },
  client: { background: "var(--color-surface)", border: "1px solid var(--color-border)" },
};

function noteDate(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Salt görüntü: mesaj listesi (sunucu render testi bunu doğrudan çizer). */
export function NoteThread(props: {
  notes: KpiValueNoteDto[];
  audience: NotesAudience;
}): JSX.Element {
  const { notes, audience } = props;
  const C = KPI_NOTES_COPY[audience];
  if (notes.length === 0) {
    return (
      <div
        data-testid="kpi-note-thread-empty"
        className="text-xs"
        style={{ color: "var(--color-text-3)" }}
      >
        {C.empty}
      </div>
    );
  }
  return (
    <ol
      data-testid="kpi-note-thread"
      aria-label={C.title}
      className="text-xs"
      style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}
    >
      {notes.map((n) => {
        const tone = noteTone(n);
        // "Danışman yanıtı:" öneki yalnız müşteri yüzeyinde; danışman ekranında yazar adı görünür
        const head =
          audience === "portal" && tone === "consultant"
            ? KPI_NOTES_COPY.portal.consultantReply
            : noteAuthorLabel(n, audience);
        return (
          <li
            key={n.id}
            data-testid="kpi-note"
            data-author-kind={n.authorKind}
            className={`kpi-note kpi-note--${tone}`}
            style={{ ...TONE_STYLE[tone], borderRadius: 8, padding: "6px 8px" }}
          >
            <div style={{ color: "var(--color-text-3)" }}>
              <b style={{ color: "var(--color-text-2)" }}>{head}</b> · {noteDate(n.createdAt)}
            </div>
            <div style={{ color: "var(--color-text)", whiteSpace: "pre-wrap" }}>{n.body}</div>
          </li>
        );
      })}
    </ol>
  );
}

export function KpiCellNotes(props: {
  audience: NotesAudience;
  setId: string;
  cell: NoteCellRef;
  /** yazma alanı (portal değer editöründe K10 alanı yazar → false) */
  composer: boolean;
  /** thread okundu/yazıldı → hücre özeti yeniden çekilir */
  onChanged?: (() => void) | undefined;
  /** yazma alanı yokken (composer=false) "Not ekle" düğmesi: iş parçacığı penceresini açar (klavye yolu) */
  onAdd?: (() => void) | undefined;
  autoFocus?: boolean;
}): JSX.Element {
  const { audience, setId, cell, composer, onChanged } = props;
  const C = KPI_NOTES_COPY[audience];
  const [notes, setNotes] = useState<KpiValueNoteDto[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const requestId = useRef(new NoteRequestId());
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const cellKey = `${setId}|${cell.metricId}|${cell.entityId}|${cell.year.toString()}`;

  useEffect(() => {
    let alive = true;
    setNotes(null);
    setLoadError(null);
    listCellNotes(audience, setId, cell)
      .then((list) => {
        if (!alive) return;
        setNotes(list);
        // karşı tarafın mesajı varsa okundu işaretle → özet noktası söner
        const counterpart = audience === "portal" ? "internal" : "client";
        if (list.some((n) => n.authorKind === counterpart)) {
          void markCellNotesSeen(audience, setId, cell)
            .then(() => onChangedRef.current?.())
            .catch(() => undefined);
        }
      })
      .catch((e: unknown) => {
        if (alive) setLoadError(noteErrorMessage(e, audience, "loadFailed"));
      });
    return () => {
      alive = false;
    };
  }, [audience, cellKey, reloadKey]);

  useEffect(() => {
    if (props.autoFocus && composer && notes !== null) fieldRef.current?.focus();
  }, [props.autoFocus, composer, notes !== null]);

  async function send(): Promise<void> {
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    setSendError(null);
    setSent(false);
    // yeniden denemede AYNI kimlik (çift gönderim tekilleşir); başarıda sıfırlanır
    try {
      const note = await createCellNote(audience, setId, cell, body, requestId.current.current());
      requestId.current.reset();
      setText("");
      setSent(true);
      setNotes((prev) => {
        const list = prev ?? [];
        return list.some((n) => n.id === note.id) ? list : [...list, note];
      });
      onChangedRef.current?.();
    } catch (e) {
      setSendError(noteErrorMessage(e, audience, "sendFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-kpi-notes data-testid="kpi-cell-notes" aria-label={C.title}>
      <div className="text-xs font-semibold mb-1" style={{ color: "var(--color-text)" }}>
        {C.title}
        {notes && notes.length > 0 ? ` (${notes.length.toString()})` : ""}
      </div>
      {loadError ? (
        <div role="alert" className="text-xs flex items-center gap-2">
          <span style={{ color: "var(--color-error)" }}>{loadError}</span>
          {loadError === C.loadFailed ? (
            <button
              type="button"
              className="underline"
              style={{ color: "var(--color-primary)" }}
              onClick={() => setReloadKey((k) => k + 1)}
            >
              {C.retry}
            </button>
          ) : null}
        </div>
      ) : notes === null ? (
        <div role="status" className="text-xs" style={{ color: "var(--color-text-3)" }}>
          {C.loading}
        </div>
      ) : (
        <NoteThread notes={notes} audience={audience} />
      )}
      {!composer && props.onAdd ? (
        <button
          type="button"
          data-testid="kpi-note-add"
          onClick={props.onAdd}
          className="mt-2 text-xs underline"
          style={{ color: "var(--color-primary)", minHeight: 28 }}
        >
          {C.addNote}
        </button>
      ) : null}
      {composer ? (
        <div className="mt-2">
          <textarea
            ref={fieldRef}
            data-testid="kpi-note-field"
            aria-label={C.placeholder}
            placeholder={C.placeholder}
            maxLength={2000}
            value={text}
            style={{ ...inp, minHeight: 56, resize: "vertical", width: "100%" }}
            onChange={(e) => {
              setText(e.target.value);
              requestId.current.reset(); // metin değişti → yeni istek kimliği
              setSent(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                e.stopPropagation();
                void send();
              }
            }}
          />
          <div className="flex items-center gap-2 mt-1">
            <button
              type="button"
              data-testid="kpi-note-send"
              disabled={busy || text.trim() === ""}
              onClick={() => void send()}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold"
              style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
            >
              {audience === "portal" ? KPI_NOTES_COPY.portal.send : KPI_NOTES_COPY.internal.reply}
            </button>
            {sent ? (
              <span role="status" className="text-xs" style={{ color: "var(--color-success)" }}>
                ✓ {C.sent}
              </span>
            ) : null}
          </div>
          {sendError ? (
            <div role="alert" className="text-xs mt-1" style={{ color: "var(--color-error)" }}>
              {sendError}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
